import { useState } from 'react';
import { Button, Card, Collapse, Divider } from '@mui/material';
import { Box, Stack, Typography } from '../components/mui-system';
import { loaderStateNames, type MetricsLoaderState } from './metrics-loader-state';
import { readableDate, sourceColors, sourceNames } from './search-metrics-presentation';

export function MetricsLoaderCard({
  state,
  observedAt,
  stale,
}: {
  state: MetricsLoaderState;
  observedAt: string | null;
  stale: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  if (!state.rows.length) return null;
  const title = stale
    ? 'Статус не обновлён'
    : state.running
      ? 'Идёт сбор'
      : state.queued
        ? 'Ожидает очереди'
        : state.attention
          ? 'Требует внимания'
          : 'Нет активных задач';
  return (
    <Card variant="outlined" component="section" aria-label="Загрузчик метрик" sx={{ p: 2 }}>
      <Stack gap={1.25}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography fontWeight={700}>Загрузчик</Typography>
          <Button
            size="small"
            aria-label="Подробнее о загрузке"
            aria-expanded={expanded}
            aria-controls="metrics-loader-details"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Свернуть' : 'Подробнее'}
          </Button>
        </Stack>
        <Stack role="status" gap={0.75}>
          <Typography
            fontSize={13}
            color={stale || state.attention ? 'warning.main' : 'text.secondary'}
          >
            {title}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0.75, sm: 3 }} flexWrap="wrap">
            {state.providers.map((provider) => (
              <Stack key={provider.provider} direction="row" alignItems="center" gap={0.75}>
                <Box
                  width={7}
                  height={7}
                  sx={{ borderRadius: '50%', bgcolor: sourceColors[provider.provider] }}
                />
                <Typography fontSize={13}>
                  {sourceNames[provider.provider]}: выполняется {provider.running} · в очереди{' '}
                  {provider.queued}
                </Typography>
              </Stack>
            ))}
            <Typography fontSize={13} color={state.attention ? 'warning.main' : 'text.secondary'}>
              Требуют внимания: {state.attention}
            </Typography>
          </Stack>
        </Stack>
        <Typography fontSize={12} color="text.secondary">
          По источникам сайтов текущего фильтра. Сохранённые метрики доступны.
          {observedAt && ` Статус получен в ${new Date(observedAt).toLocaleTimeString('ru-RU')}.`}
          {stale && ' Не удалось обновить статус; показаны последние полученные сведения.'}
        </Typography>
        <Collapse in={expanded} unmountOnExit>
          <Stack
            id="metrics-loader-details"
            gap={1.5}
            divider={<Divider />}
            sx={{ maxHeight: 380, overflow: 'auto', pt: 1 }}
          >
            <Typography fontSize={12} color="text.secondary">
              Обработанные части включают ошибки. Завершение порции не означает, что вся история
              загружена.
            </Typography>
            {state.rows.map(({ siteKey, source, run, progress, problems, attention }) => (
              <Stack key={source.property.id} gap={0.5} sx={{ overflowWrap: 'anywhere' }}>
                <Typography fontSize={13} fontWeight={700}>
                  {siteKey} · {sourceNames[source.property.provider]}
                </Typography>
                <Typography fontSize={12} color={attention ? 'warning.main' : 'text.secondary'}>
                  {run
                    ? (loaderStateNames[run.status] ?? 'Состояние неизвестно')
                    : 'Сбор ещё не запускался'}
                  {run && ` · ${readableDate(run.dateFrom)} — ${readableDate(run.dateTo)}`}
                </Typography>
                {run &&
                  (progress ? (
                    <Typography fontSize={12} color="text.secondary">
                      {progress.dimensions.join(', ')} · Обработано частей: {progress.processed} из{' '}
                      {progress.total} · с ошибками: {progress.failed}
                    </Typography>
                  ) : (
                    <Typography fontSize={12} color="text.secondary">
                      Прогресс порции недоступен.
                    </Typography>
                  ))}
                {problems.map((problem) => (
                  <Typography key={problem} fontSize={12} color="warning.main">
                    {problem}
                  </Typography>
                ))}
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Stack>
    </Card>
  );
}
